import { useState, useMemo } from 'react'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { usePollAISuggestionRequest } from 'hooks/queries/aiSuggestionRequests/usePollAISuggestionRequest'
import { useVisualStyles } from 'hooks/queries/visualStyles'
import { FontPairing, useFontPairings } from 'hooks/queries/fontPairings'
import { SuggestionRequestStatus } from 'types'
import { MediaFile } from 'components/VisualStylePreviewCarousel/VisualStylePreviewCarousel'
import { SelectionStage } from './SelectionStage'
import { GenerationStage } from './GenerationStage'
import { StyleGenerated } from './GenerationStage'

interface RecommendationResult {
  visual_style_ids?: number[]
  font_pairing_ids?: number[]
}

export interface BrandVisualStyleSelectionProps {
  onNext: (fontPairings: FontPairing[], generatedStyles: StyleGenerated[], sourceImage: MediaFile | null) => void
  onBack: () => void
  brandKitId?: number
}

export const BrandVisualStyleSelection = ({ onNext, onBack, brandKitId }: BrandVisualStyleSelectionProps) => {
  const [stage, setStage] = useState<'selection' | 'generation'>('selection')
  const [sourceImage, setSourceImage] = useState<MediaFile | null>(null)
  const [generatedStyles, setGeneratedStyles] = useState<StyleGenerated[]>([])

  // Fetch brand kit with brand visual analysis
  // We poll until the recommendation request ID becomes available
  const { data: brandKitData } = useBrandKit(brandKitId, {
    includeBrandVisualAnalysis: true,
    enabled: !!brandKitId,
    refetchInterval: (data) => {
      // Poll every 1s until we have the recommendation request ID
      const requestId =
        data?.brand_kit?.brand_visual_kit?.brand_visual_analysis?.result?.recommend_styles_and_fonts_request_id
      return requestId ? false : 1000
    },
  })

  const brandKit = brandKitData?.brand_kit
  const brandVisualKit = brandKit?.brand_visual_kit
  const brandVisualAnalysis = brandVisualKit?.brand_visual_analysis
  const recommendRequestId = brandVisualAnalysis?.result?.recommend_styles_and_fonts_request_id

  // Poll for AI recommendations
  const { data: recommendationData } = usePollAISuggestionRequest(
    { id: recommendRequestId || 0 },
    { enabled: !!recommendRequestId },
  )

  const recommendationsReady = recommendationData?.request?.status === SuggestionRequestStatus.Successful

  // Parse the generated content to extract recommendations
  const recommendationResult = useMemo((): RecommendationResult | undefined => {
    if (!recommendationsReady || !recommendationData?.request?.generated_content) return undefined
    try {
      return JSON.parse(recommendationData.request.generated_content) as RecommendationResult
    } catch {
      return undefined
    }
  }, [recommendationsReady, recommendationData?.request?.generated_content])

  // Fetch all visual styles and font pairings
  const { data: visualStylesData } = useVisualStyles()
  const { data: fontPairingsData } = useFontPairings()

  // Get the 3 styles to display (recommended if available, otherwise first 3)
  const displayedVisualStyles = useMemo(() => {
    if (!visualStylesData || visualStylesData.length === 0) return []

    const recommendedIds = recommendationResult?.visual_style_ids ?? []

    // Get base styles: recommended ones if available, otherwise first 3
    let baseStyles =
      recommendedIds.length > 0
        ? recommendedIds
            .map((id) => visualStylesData.find((s) => s.id === id))
            .filter((s): s is NonNullable<typeof s> => !!s)
            .slice(0, 3)
        : visualStylesData.slice(0, 3)

    return baseStyles
  }, [visualStylesData, recommendationResult])

  // Get the 3 font pairings to display (recommended if available, otherwise first 3)
  const displayedFontPairings = useMemo(() => {
    if (!fontPairingsData || fontPairingsData.length === 0) return []

    const recommendedIds = recommendationResult?.font_pairing_ids ?? []

    // Get base font pairings: recommended ones if available, otherwise first 3
    let basePairings =
      recommendedIds.length > 0
        ? recommendedIds
            .map((id) => fontPairingsData.find((fp) => fp.id === id))
            .filter((fp): fp is NonNullable<typeof fp> => !!fp)
            .slice(0, 3)
        : fontPairingsData.slice(0, 3)

    return basePairings
  }, [fontPairingsData, recommendationResult])

  if (stage === 'selection') {
    return (
      <SelectionStage
        onNext={() => {
          onNext(displayedFontPairings, generatedStyles, null)
        }}
        onSelectImage={(image) => {
          setSourceImage(image)
          setStage('generation')
        }}
        onBack={onBack}
      />
    )
  }

  if (stage === 'generation' && sourceImage && brandKitId) {
    return (
      <GenerationStage
        onNext={() => onNext(displayedFontPairings, generatedStyles, sourceImage)}
        onBack={() => setStage('selection')}
        sourceImage={sourceImage}
        visualStyles={displayedVisualStyles}
        brandKitId={brandKitId}
        onStylesGenerated={setGeneratedStyles}
      />
    )
  }

  return null
}
